const NIL_UUID = '00000000-0000-0000-0000-000000000000'

/** 变更路径缺失：不是「没有发布证据」。历史行也可能还写着旧摘要。 */
export const PATHS_MISSING_SUMMARY = '变更路径缺失，巡检无法核对'
export const RECEIPT_UNAVAILABLE_SUMMARY = '没有可核验的发布证据，没有模型结论。'

const RECEIPT_EVIDENCE_FIELDS = [
  'deployment_sha',
  'receipt_digest',
  'affected_services',
  'health_ok',
  'smoke_ok',
  'rollback_ready',
] as const

export function isPresentUUID(value: string | null | undefined): boolean {
  const id = (value ?? '').trim()
  return id !== '' && id !== NIL_UUID
}

/** items 为空时：没跑过是 idle，跑完没问题是 clean。有条目则不是空态。 */
export function inspectionEmptyKind(
  itemCount: number,
  lastCompletedAt?: string | null,
): 'idle' | 'clean' | null {
  if (itemCount > 0) return null
  return lastCompletedAt ? 'clean' : 'idle'
}

/**
 * 修复要打到真实开发任务。零 UUID / 缺 task_id / 缺 finding id 都不能点——
 * 那不是可修的任务，testid 也不许撞成 inspection-repair-00000000-…。
 */
export function inspectionRepairTaskId(item: {
  id?: string | null
  open?: boolean
  task_id?: string | null
}): string | null {
  if (!item.open) return null
  if (!isPresentUUID(item.id) || !isPresentUUID(item.task_id)) return null
  return item.task_id!.trim()
}

type InspectionGroupable = {
  summary: string
  evidence: string
  rule?: string | null
  created_at?: string | null
}

/** evidence 只点名 changed_paths，没有制品/部署/在线回执字段。 */
export function isPathsMissingFinding(item: {
  evidence?: string | null
  summary?: string | null
}): boolean {
  const evidence = (item.evidence ?? '').toLowerCase()
  if (!evidence.includes('changed_paths')) return false
  return !RECEIPT_EVIDENCE_FIELDS.some((field) => evidence.includes(field))
}

/**
 * 主行标题。路径缺口永远说「变更路径缺失…」；
 * 「没有可核验的发布证据」只留给真缺回执的行。
 */
export function inspectionFindingSummary(item: {
  summary: string
  evidence?: string | null
}): string {
  if (isPathsMissingFinding(item)) return PATHS_MISSING_SUMMARY
  return item.summary
}

/** DeepSeek 占位 / 噪声：收成一组时用，避免刷屏。 */
export function isInspectionNoiseFinding(item: { rule?: string | null }): boolean {
  const rule = (item.rule ?? '').trim()
  return (
    rule === 'deepseek.context_unavailable' ||
    rule === 'deepseek.unavailable' ||
    rule.endsWith('.context_unavailable') ||
    rule.endsWith('.unavailable')
  )
}

function groupKey(item: InspectionGroupable): string {
  const summary = inspectionFindingSummary(item)
  if (isInspectionNoiseFinding(item) || isPathsMissingFinding(item)) {
    // 噪声/路径缺口按展示摘要 + evidence 合组，rule 差异不拆开。
    return `${summary}\0${item.evidence}`
  }
  return `${summary}\0${item.evidence}`
}

/**
 * 连续且展示摘要+evidence 相同的发现收成一组。不同摘要不合组。
 * 每条仍在组里，只是主行不再刷成六十张一样的卡片。
 */
export function groupInspectionFindings<T extends InspectionGroupable>(items: T[]): T[][] {
  const groups: T[][] = []
  for (const item of items) {
    const last = groups[groups.length - 1]
    const head = last?.[0]
    if (head && groupKey(head) === groupKey(item)) {
      last.push(item)
      continue
    }
    groups.push([item])
  }
  return groups
}

/** 组里最新的一条：created_at 更晚；并列时保留列表里先出现的（接口已按新到旧排）。 */
export function inspectionGroupNewest<T extends InspectionGroupable>(items: T[]): T {
  const first = items[0]
  if (!first) {
    throw new Error('inspectionGroupNewest requires a non-empty group')
  }
  let newest = first
  let newestMs = createdAtMs(first)
  for (let i = 1; i < items.length; i += 1) {
    const item = items[i]!
    const ms = createdAtMs(item)
    if (ms > newestMs) {
      newest = item
      newestMs = ms
    }
  }
  return newest
}

function createdAtMs(item: InspectionGroupable): number {
  const ms = Date.parse(item.created_at ?? '')
  return Number.isFinite(ms) ? ms : 0
}

/** 主行上的次数。短标签，不是新的 copy 模板。 */
export function inspectionGroupCountLabel(count: number): string {
  return `${count} 次`
}
